/**
 * FAILURE LAB — Code101 chapter 7, where the other six chapters come back as symptoms.
 *
 * FOUNDER, 2026-08-14: "ch7 next".
 *
 * ── THE SYNTHESIS IS REAL, NOT DECORATIVE ─────────────────────────────────────────────────────
 * §2 is "a tour back through Days 1–6" and every family names its day. A lab was built for each of
 * those five days before this chapter was reached, so a learner arriving here has already WATCHED
 * every family fail with their own hands. This lab does not re-explain them; it names each one and
 * points back at the instrument where it happened.
 *
 * ── THE SYMPTOM NEVER CHANGES, AND THAT IS THE WHOLE UI ───────────────────────────────────────
 * Pick one family or three; the user-visible line is the same sentence every time — his sentence:
 * "the app isn't working." A UI that produced a different symptom per family would teach that you
 * can diagnose from the outside, which is precisely the belief §3 is written to break. So the
 * symptom box is deliberately, monotonously identical, and the diagnosis panel is where everything
 * happens.
 *
 * ── IT DOES NOT MARK THE LEARNER'S ANSWER ─────────────────────────────────────────────────────
 * §4 says "Most well-built systems use all four, situationally." So the four responses are offered
 * as his four quotes and the lab never says which is right. There is no score anywhere in this
 * chapter's instrument: §5's own line is "The tests prove the things you thought to check. The
 * world will check the things you forgot" — a lab that graded a failure-tree would be claiming to
 * be the world.
 */

import { useState } from 'react';
import { Value } from '../../../packages/ankr-glass/src/react.jsx';
import { computed, cited } from '../../../packages/ankr-provenance/src/provenance.mjs';
import {
  FAMILIES, HIS_COMPOUND, CHANNELS, RESPONSES, PAYERS, incident,
  NOT_TWO_STATES, SINGLE_MISTAKE, OBSERVATION, NO_EYES, ALL_FOUR, SOMEONE_PAYS, TESTS_PROVE, AI_FAILS,
  SOURCE_1, SOURCE_2, SOURCE_3, SOURCE_4, SOURCE_5, SOURCE_6, SOURCE_7,
} from './failure.mjs';

export default function FailureLab() {
  const [picked, setPicked] = useState([]);
  const [channel, setChannel] = useState(null);
  const [response, setResponse] = useState(null);

  const inc = incident(picked);
  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  return (
    <div className="g-glass shell-panel lab">
      <div className="g-label">Failure lab · the five families</div>
      <h2 className="g-h2 lab-h">Every one of them looks the same from outside</h2>
      <p className="g-body">
        You have already watched all five of these fail — one in each of the last five chapters.
        Build an incident out of them and read the only thing your user will ever tell you.
      </p>
      <p className="g-body g-dim"><Value of={cited(SINGLE_MISTAKE, SOURCE_1)} /></p>

      {/* ── THE FIVE, EACH POINTING BACK AT ITS OWN LAB ── */}
      <div className="fai-grid">
        {FAMILIES.map((f) => (
          <button key={f.id} type="button"
            className={`fai-card ${picked.includes(f.id) ? 'fai-on' : ''}`}
            aria-pressed={picked.includes(f.id)} onClick={() => toggle(f.id)}>
            <div className="fai-head"><b>{f.name}</b><span className="fai-day">Day {f.day}</span></div>
            <p className="g-body fai-what">{f.what}</p>
            <p className="g-body g-dim fai-seen">You saw this in {f.lab}: {f.seen}</p>
          </button>
        ))}
      </div>

      <div className="lab-task-head">
        <button className="g-btn" onClick={() => setPicked(HIS_COMPOUND.families)}>
          Use the module's own example
        </button>
        <button className="g-btn" onClick={() => { setPicked([]); setChannel(null); setResponse(null); }}>
          Clear
        </button>
      </div>

      {/* ── THE SYMPTOM. Identical, always. ── */}
      <div className="lab-task">
        <div className="g-label">What your user tells you</div>
        <div className="fai-symptom">“{inc.symptom}”</div>
        {inc.count === 0 && <p className="g-body g-dim">Pick one or more families above.</p>}
        {inc.count > 0 && (
          <p className="g-body fai-same">
            That sentence is the same whether you picked one family or all five. It carries no
            information about which. <b>This is why observation exists.</b>
          </p>
        )}
        {inc.compound && picked.length === 3 && HIS_COMPOUND.families.every((f) => picked.includes(f)) && (
          <p className="g-body g-dim"><Value of={cited(HIS_COMPOUND.quote, SOURCE_2)} /></p>
        )}
      </div>

      {/* ── THE DIAGNOSIS ── */}
      {inc.count > 0 && (
        <div className="lab-task">
          <div className="g-label">What is actually happening</div>
          <div className="lab-readout">
            <div><span className="g-label">Families involved</span>
              <Value of={computed(String(inc.count))} /></div>
            <div><span className="g-label">Chapters they came from</span>
              <Value of={computed(inc.days.map((d) => `Day ${d}`).join(' · '))} /></div>
          </div>
          <ul className="fai-list">
            {inc.families.map((f) => (
              <li key={f.id}><b>{f.name}</b> — {f.detail}</li>
            ))}
          </ul>
          {inc.compound && (
            <p className="g-body fai-compound">
              {inc.count} families compounding into one sentence. Fixing any one of them may leave
              the user saying exactly the same thing tomorrow.
            </p>
          )}

          {/* §3 — the channels. Offered, not marked. */}
          <div className="fai-sub">
            <div className="g-label">Which channel would have caught it first?</div>
            <div className="lab-task-head">
              {CHANNELS.map((c) => (
                <button key={c.id} className={`g-btn ${channel === c.id ? 'g-btn-primary' : ''}`}
                  onClick={() => setChannel(c.id)}>{c.name}</button>
              ))}
            </div>
            {channel && (
              <p className="g-body g-dim">
                <Value of={cited(CHANNELS.find((c) => c.id === channel).quote, SOURCE_3)} />
              </p>
            )}
            <p className="g-body"><Value of={cited(OBSERVATION, SOURCE_3)} /></p>
            {channel === 'reports' && <p className="g-body fai-warn"><Value of={cited(NO_EYES, SOURCE_3)} /></p>}
          </div>

          {/* §4 — the responses. Four quotes and no verdict. */}
          <div className="fai-sub">
            <div className="g-label">And what should the system do next?</div>
            <div className="lab-task-head">
              {RESPONSES.map((x) => (
                <button key={x.id} className={`g-btn ${response === x.id ? 'g-btn-primary' : ''}`}
                  onClick={() => setResponse(x.id)}>{x.name}</button>
              ))}
            </div>
            {response && (
              <p className="g-body g-dim">
                <Value of={cited(RESPONSES.find((x) => x.id === response).quote, SOURCE_4)} />
              </p>
            )}
            <p className="g-body g-dim">
              <Value of={cited(ALL_FOUR, SOURCE_4)} /> Nothing here marks your choice, because the
              module gives four tools and no rule for picking between them — that judgement is the
              thing you are here to build.
            </p>
          </div>
        </div>
      )}

      <div className="lab-task">
        <div className="g-label">Who pays</div>
        <p className="g-body"><Value of={cited(SOMEONE_PAYS, SOURCE_7)} /></p>
        <ul className="fai-list">{PAYERS.map((p) => <li key={p}>{p}</li>)}</ul>
      </div>

      <p className="g-body fai-punch"><Value of={cited(NOT_TWO_STATES, SOURCE_1)} /></p>

      <p className="g-body g-dim lab-why">
        There is no score in this lab and there will not be one.
        <Value of={cited(TESTS_PROVE, SOURCE_5)} /> An instrument that graded your failure tree
        would be claiming to be the world.
        <br />
        §6's five AI failure modes — hallucination, stale knowledge, wrong tool call, confident but
        wrong, refusing the right ask — are named in the reading and not modelled here:
        <Value of={cited(AI_FAILS, SOURCE_6)} /> They are the subject of the next course, not a
        sixth family in this one.
      </p>
    </div>
  );
}
